import React from "react";
import {
  AlertBox,
  LoadingSpinner,
  PrimaryButton,
  SecondaryButton,
  SoftButton,
} from "../UI/DesignStandards";
import SquadSelect from "./ScoringToolbar/SquadSelect";
import { namedSignupEntries } from "../../utility/extractSignupSheets";
import { pendingSignupReviewEntries } from "../../utility/signupReview";

const fileList = (files) => Array.from(files || []);

const countLabel = (count, one, many) =>
  count === 1 ? `1 ${one}` : `${count} ${many}`;

const ScoringUploadPanel = ({
  title,
  description,
  files,
  onFilesChange,
  onCancel,
  squads,
  squadId,
  onSquadChange,
  accept,
  onExtract,
  extracting = false,
  extractProgress = null,
  extractError = "",
  extractWarning = "",
  extractWarningTitle = "No names found",
  extractFinishedLabel = "",
  extractSheets = null,
  extractSummary = null,
  events = [],
  setup = null,
  onReviewTeams,
  onReviewScores,
  reviewCount = 0,
  onVerifyAverages,
  averagesPending = false,
  averageWaitNote = "",
  onImportAnother,
  fileResetKey = 0,
}) => {
  const selected = fileList(files);
  const showSquad = Array.isArray(squads);
  const padEntries = (extractSheets || []).flatMap((sheet) =>
    namedSignupEntries(sheet.extraction, { teamSize: sheet.teamSize }).map(
      (entry) => ({
        ...entry,
        fileName: sheet.fileName,
        eventName: sheet.eventName,
      })
    )
  );
  const entries = extractSheets?.length
    ? pendingSignupReviewEntries(extractSheets, { events, setup }).map((entry) => ({
        ...entry,
        fileName: entry.sheet?.fileName,
      }))
    : padEntries;
  const skipped = extractSummary?.skipped || [];
  const redirects = extractSummary?.redirects || [];
  const warningDetail =
    extractWarning && typeof extractWarning === "object" ? extractWarning : null;
  const warningTitle = warningDetail?.title || extractWarningTitle;
  const warningType = warningDetail?.type || "warning";
  const warningLines = Array.isArray(warningDetail?.lines)
    ? warningDetail.lines.filter(Boolean)
    : [];
  const warningMessage = warningDetail ? "" : extractWarning;
  const canExtract = selected.length > 0 && (!showSquad || Boolean(squadId));
  const extractDone = Number(extractProgress?.done) || 0;
  const extractTotal = Number(extractProgress?.total) || 0;
  const extractStatus = !extracting
    ? ""
    : extractDone > 0 && extractTotal > 0
      ? `Extracting ${extractDone} of ${extractTotal}. This can take a minute.`
      : "Reading the sheet. This can take a minute.";
  const extractButtonLabel =
    extractDone > 0 && extractTotal > 0
      ? `Extracting ${extractDone} of ${extractTotal}`
      : extracting
        ? "Extracting"
        : "Extract";
  const importedTeams = Number(extractSummary?.importedTeams) || 0;
  const importedScores = Number(extractSummary?.importedScores) || 0;
  const showSignupResult = !extracting && entries.length > 0;
  const showRecapResult =
    !extracting && Boolean(extractSummary) && importedScores > 0;
  const showResult = showSignupResult || showRecapResult;

  return (
    <div className="card-board p-6 mb-6">
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="heading-display text-xl">{title}</h2>
            {!showResult && description ? (
              <p className="text-sm text-brand-muted mt-1">{description}</p>
            ) : null}
          </div>
          <SecondaryButton
            type="button"
            onClick={onCancel}
            disabled={extracting}
          >
            {showResult ? "Done" : "Cancel"}
          </SecondaryButton>
        </div>
        {extractFinishedLabel && !extracting ? (
          <p
            key={extractFinishedLabel}
            role="status"
            aria-live="polite"
            className="text-sm font-medium text-brand-charcoal bg-brand-gold/25 border border-brand-gold rounded-lg px-3 py-2"
          >
            {extractFinishedLabel}
          </p>
        ) : null}
        {extractError ? (
          <AlertBox
            type="error"
            title="Could not extract"
            message={extractError}
          />
        ) : null}
        {warningMessage || warningLines.length ? (
          <AlertBox type={warningType} title={warningTitle}>
            {warningLines.length ? (
              <ul className="space-y-2 text-sm list-disc pl-4">
                {warningLines.map((line, index) => (
                  <li key={`${line}-${index}`}>{line}</li>
                ))}
              </ul>
            ) : (
              <div>{warningMessage}</div>
            )}
          </AlertBox>
        ) : null}

        {showSignupResult ? (
          <div className="space-y-3">
            <h3 className="heading-display text-lg">
              {countLabel(entries.length, "signup", "signups")} ready to review
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              {onReviewTeams ? (
                <PrimaryButton type="button" onClick={onReviewTeams}>
                  Review signups
                </PrimaryButton>
              ) : null}
              {onImportAnother ? (
                <SecondaryButton type="button" size="sm" onClick={onImportAnother}>
                  Import another
                </SecondaryButton>
              ) : null}
            </div>
            <ul className="divide-y divide-brand-charcoal/10 border border-brand-charcoal/15 rounded-lg overflow-hidden">
              {entries.map((entry, index) => (
                <li
                  key={`${entry.fileName}-${entry.key || index}`}
                  className="px-3 py-2 text-sm text-brand-charcoal"
                >
                  <div className="font-medium">
                    {(entry.rows || entry.bowlers || [])
                      .map((bowler) => bowler.name)
                      .join(" · ")}
                  </div>
                  <div className="text-xs text-brand-muted">
                    {[
                      entry.eventName,
                      entry.teamName,
                      entry.lane ? `Lane ${entry.lane}` : null,
                      entry.fileName,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {showRecapResult ? (
          <div className="space-y-3">
            <h3 className="heading-display text-lg">
              {countLabel(importedTeams, "team", "teams")} imported
            </h3>
            <p className="text-sm text-brand-muted">
              {countLabel(importedScores, "game score", "game scores")} written.
              {averagesPending
                ? " Verify averages, then verify scores on Scoring."
                : " Verify scores on Scoring."}
            </p>
            {averagesPending && averageWaitNote ? (
              <p className="text-sm text-brand-charcoal">{averageWaitNote}</p>
            ) : null}
            {redirects.map((row, index) => (
              <p
                key={`${row.toLabel || "team"}-${index}`}
                className="text-sm text-brand-charcoal"
              >
                Scores were written to {row.toLabel} instead of {row.fromLabel}.
                The BTA Use on the sheet does not match those bowlers.
              </p>
            ))}
            <div className="flex flex-wrap items-center gap-2">
              {averagesPending && onVerifyAverages ? (
                <PrimaryButton type="button" onClick={onVerifyAverages}>
                  Verify averages
                </PrimaryButton>
              ) : null}
              {!averagesPending && onReviewScores && reviewCount > 0 ? (
                <PrimaryButton type="button" onClick={onReviewScores}>
                  Review recaps ({reviewCount.toLocaleString("en-US")})
                </PrimaryButton>
              ) : null}
              {averagesPending && onReviewScores && reviewCount > 0 ? (
                <SoftButton color="cream" size="sm" onClick={onReviewScores}>
                  Review recaps ({reviewCount.toLocaleString("en-US")})
                </SoftButton>
              ) : null}
              {onImportAnother ? (
                <SecondaryButton type="button" size="sm" onClick={onImportAnother}>
                  Import another
                </SecondaryButton>
              ) : null}
            </div>
            {skipped.length ? (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-brand-charcoal">
                  Not imported
                </h4>
                <ul className="divide-y divide-brand-charcoal/10 border border-brand-charcoal/15 rounded-lg overflow-hidden">
                  {skipped.map((row, index) => (
                    <li
                      key={`${row.label || row.teamCode}-${index}`}
                      className="px-3 py-2 text-sm text-brand-charcoal"
                    >
                      <div className="font-medium">
                        {row.label || row.teamCode}
                      </div>
                      <div className="text-xs text-brand-muted">
                        {[row.reason, row.fileName].filter(Boolean).join(" · ")}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}

        {!showResult ? (
          <>
            {showSquad ? (
              <SquadSelect
                label="Squad"
                squads={squads || []}
                value={squadId || ""}
                onChange={onSquadChange}
                required
                disabled={extracting}
              />
            ) : null}
            <label className="block">
              <span className="block text-sm font-medium text-brand-charcoal mb-1">
                Files
              </span>
              <input
                key={fileResetKey}
                type="file"
                multiple
                accept={accept}
                onChange={(event) => onFilesChange(event.target.files)}
                disabled={extracting}
                className="block w-full text-sm text-brand-charcoal file:mr-4 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-brand-gold file:font-display file:font-bold file:text-brand-charcoal"
              />
            </label>
            {onExtract ? (
              <>
                {extracting ? (
                  <div
                    className="flex items-center gap-3"
                    role="status"
                    aria-live="polite"
                  >
                    <LoadingSpinner size="lg" />
                    <p className="text-sm text-brand-charcoal">{extractStatus}</p>
                  </div>
                ) : null}
                <p className="text-sm text-brand-muted">
                  Uses AI for extraction. AI can make mistakes.
                </p>
                <PrimaryButton
                  type="button"
                  onClick={onExtract}
                  loading={extracting}
                  disabled={!canExtract || extracting}
                >
                  {extractButtonLabel}
                </PrimaryButton>
              </>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default ScoringUploadPanel;
